
import React, { useState } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { MenuItem, Language } from '../types';
import { StarIcon, FireIcon, LeafIcon } from './icons';
import { Plus, Heart } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useSettings } from '../src/contexts/SettingsContext';
import { t } from '../translations';
import { DEFAULT_MENU_ITEM_IMAGE } from '../src/constants';
import { formatPrice } from '../src/utils/formatPrice';
import { haptic } from '../src/utils/haptics';
import { useFavourites } from '../src/utils/useFavourites';

interface MenuItemCardProps {
  item: MenuItem;
  language: Language;
  isRecommended: boolean;
  onSelect: () => void;
  isFeatured: boolean;
}

const Tag: React.FC<{tag: string, language: Language}> = ({ tag, language }) => {
    const tagContent: Record<string, {icon: React.ReactElement, text: string, className: string}> = {
        'best-seller': { icon: <StarIcon className="w-3 h-3"/>, text: t('filters.bestSeller', language), className: "bg-accent-10 text-accent" },
        'spicy': { icon: <FireIcon className="w-3 h-3"/>, text: t('filters.spicy', language), className: "bg-danger-bg-50 text-danger-fg" },
        'vegetarian': { icon: <LeafIcon className="w-3 h-3"/>, text: t('filters.vegetarian', language), className: "bg-success-bg-50 text-success-fg" },
    }
    const style = tagContent[tag];
    if(!style) return null;

    return (
        <div className={`flex items-center space-x-1 text-[10px] font-bold px-2 py-1 rounded-full ${style.className}`}>
            {style.icon}
            <span>{style.text}</span>
        </div>
    )
}

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 200, damping: 25 } }
};

const MenuItemCard: React.FC<MenuItemCardProps> = ({ item, language, isRecommended, onSelect, isFeatured }) => {
  const { cart, addToCart, removeFromCart } = useCart();
  const { settings } = useSettings();
  const { has: isFav, toggle: toggleFav } = useFavourites();
  const [showPlusOne, setShowPlusOne] = useState(false);
  const cartItem = cart.find(i => i.id === item.id);
  const quantity = cartItem ? cartItem.quantity : 0;

  const containerClasses = 'col-span-1';

  const handleAdd = (e: React.MouseEvent) => {
      e.stopPropagation();
      haptic.light(); // короткий bump — гость физически чувствует что добавилось
      addToCart(item);
      setShowPlusOne(true);
      setTimeout(() => setShowPlusOne(false), 800);
  }

  const handleRemove = (e: React.MouseEvent) => {
      e.stopPropagation();
      removeFromCart(item.id);
  }

  return (
    <motion.div
      variants={cardVariants}
      className={`${containerClasses} relative h-full`}
    >
        <div
            onClick={onSelect}
            className={`w-full h-full text-left bg-[var(--bg-card)] rounded-2xl overflow-hidden group transition-all duration-300 shadow-sm hover:shadow-xl border ${quantity > 0 ? 'border-[var(--accent-20)]' : 'border-[var(--glass-border)]'} ${isRecommended ? 'ring-2 ring-[var(--accent-color)] shadow-[var(--accent-20)]' : ''} ${item.inStock === false ? 'opacity-70 grayscale-[0.4]' : ''}`}
        >
            <AnimatePresence>
                {showPlusOne && (
                    <motion.div
                        initial={{ opacity: 0, y: 0, scale: 0.5 }}
                        animate={{ opacity: 1, y: -60, scale: 1.2 }}
                        exit={{ opacity: 0 }}
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none"
                    >
                        <div className="w-12 h-12 bg-[var(--accent-color)] rounded-full flex items-center justify-center text-white font-black text-lg shadow-lg">
                          +1
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        <div className="flex flex-col h-full">
            <div className="w-full h-36 flex-shrink-0 relative overflow-hidden">
            <img
                src={item.image || DEFAULT_MENU_ITEM_IMAGE}
                alt={item.names?.[language] || item.names?.ru}
                className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
                loading="lazy"
                referrerPolicy="no-referrer"
                style={{ background: 'linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%)' }}
                onLoad={(e) => { (e.target as HTMLImageElement).style.background = 'none'; }}
                onError={(e) => {
                    (e.target as HTMLImageElement).src = DEFAULT_MENU_ITEM_IMAGE;
                }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>

            {/* Tag indicators on image */}
            <div className="absolute top-2 left-2 flex gap-1 z-10">
              {item.isPopular && (
                <div className="bg-white/90 backdrop-blur-sm text-[var(--accent-color)] text-[9px] font-black px-2 py-1 rounded-lg flex items-center gap-1 shadow-md">
                  <StarIcon className="w-3 h-3 fill-current" />
                  <span>TOP</span>
                </div>
              )}
              {item.tags?.includes('spicy') && (
                <div className="bg-danger/90 backdrop-blur-sm text-white w-6 h-6 rounded-lg flex items-center justify-center shadow-md" title="Spicy">
                  <FireIcon className="w-3.5 h-3.5" />
                </div>
              )}
              {item.tags?.includes('vegetarian') && (
                <div className="bg-success-bg-500/90 backdrop-blur-sm text-white w-6 h-6 rounded-lg flex items-center justify-center shadow-md" title="Vegetarian">
                  <LeafIcon className="w-3.5 h-3.5" />
                </div>
              )}
            </div>

            {item.inStock === false && (
              <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-20">
                <div className="bg-white/90 text-danger-fg text-xs font-black px-3 py-1.5 rounded-full shadow-xl transform -rotate-12 border-2 border-danger">
                  {t('dishModal.outOfStock', language).toUpperCase()}
                </div>
              </div>
            )}

            {/* Heart toggle — favourites. stopPropagation чтобы клик не открыл
                DishDetailModal. Серое сердце пустое, любимое — красное заполненное. */}
            <button
              onClick={(e) => { e.stopPropagation(); haptic.light(); toggleFav(item.id); }}
              className="absolute top-0.5 right-0.5 z-20 w-11 h-11 flex items-center justify-center active:scale-90 transition-transform"
              aria-label={isFav(item.id) ? 'Remove from favourites' : 'Add to favourites'}
            >
              {/* 44px тап-зона (a11y), визуальный кружок остаётся 32px */}
              <span className="w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md">
                <Heart
                  className={`w-4 h-4 transition-colors ${
                    isFav(item.id) ? 'fill-danger text-danger' : 'text-ink-muted'
                  }`}
                />
              </span>
            </button>
            </div>
            <div className="p-3 flex flex-col flex-1 justify-between relative">
            <div>
                {/* sans-serif для читаемости на мобиле. serif оставили для заголовков h1. */}
                <h3 className="font-semibold text-sm leading-tight text-[var(--text-primary)] mb-1 line-clamp-2">
                    {item.names?.[language] || item.names?.ru}
                </h3>
                <p className="text-[11px] text-[var(--text-secondary)] line-clamp-2 leading-relaxed">
                    {item.descriptions[language]}
                </p>
            </div>

            <div className="mt-auto pt-2 flex items-center justify-between gap-2">
                {/* Цена нейтрально-тёмная (не оранжевая): оранжевый теперь только у CTA-кнопок */}
                <p className="text-sm font-bold text-[var(--text-primary)] whitespace-nowrap">
                  {formatPrice(item.price, settings.currency)}
                </p>

                {item.inStock === false ? (
                    <div className="text-[9px] font-bold text-danger-fg uppercase tracking-wider">
                        {t('dishModal.outOfStock', language)}
                    </div>
                ) : (
                    <button
                        onClick={handleAdd}
                        aria-label="add to cart"
                        className={`relative flex items-center justify-center w-11 h-11 rounded-xl transition-all active:scale-90 font-bold ${
                            quantity > 0
                            ? 'bg-[var(--accent-color)] text-white shadow-md'
                            : 'bg-[var(--neutral-bg)] text-[var(--text-primary)] hover:bg-[var(--accent-color)] hover:text-white'
                        }`}
                    >
                        {quantity > 0 && (
                            <motion.span
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white text-[var(--accent-color)] rounded-full flex items-center justify-center text-[10px] font-black shadow-lg border-2 border-[var(--accent-color)]"
                            >
                                {quantity}
                            </motion.span>
                        )}
                        <Plus className="w-4 h-4" />
                    </button>
                )}
            </div>
            </div>
        </div>
        {/* Cart indicator — inside overflow-hidden, not absolute */}
        {quantity > 0 && (
          <div className="h-[3px] bg-[var(--accent-color)] w-full"></div>
        )}
        </div>
    </motion.div>
  );
};

export default MenuItemCard;
